import {
  JWT_TOKEN,
  LAST_ACTIVITY,
  PORTFOLIO,
  PROJECT_DASHBOARD_FILTER,
} from "./constants";

/**
 * Single source of truth for the lifecycle/scope of persisted (localStorage)
 * entries, so cross-session / cross-tenant leak-prevention stays correct as keys
 * are added. Three scopes:
 *
 *   SESSION_BOUND — credentials + tenant/environment-scoped state. MUST be wiped
 *     on LOGOUT, or a different login or tenant on the same device inherits it.
 *   USER_BOUND — per-user preferences/filters/layout/selections. Kept across a
 *     same-user logout (so re-login recovers context) but wiped on a USER SWITCH
 *     so they don't bleed across accounts.
 *   GLOBAL — everything NOT listed here: device/UI prefs with no identity
 *     (grid page sizes, language, pane widths/collapse, non-scoped view-mode
 *     toggles, one-time migration/debug flags). Never auto-cleared. NOTE:
 *     scenario-scoped view state (e.g. gsep_viewMode_{scenarioId}) is NOT global —
 *     scenario ids collide across tenants, so it lives in SESSION_BOUND below.
 *
 * A scope is { keys, prefixes, suffixes }; an entry matches if its key is in
 * `keys`, starts with any `prefixes`, or ends with any `suffixes`. Prefer
 * prefix/suffix family matchers over individual keys so new siblings (e.g. a new
 * `scheduler_filter_*` or `*ProjectsFilter-V2`) are covered automatically.
 *
 * Not listed (handled by their owners): `username` — kept for user-switch
 * detection, wiped explicitly in SecurityActions; the `offolio_ws_jobs:*` progress
 * buckets — wiped by JobPersistenceService.clearAllPersistedJobs(); the
 * `view_prefs.<viewId>.<userId>` documents (scenarioColumnPrefsStore) —
 * deliberately GLOBAL: they embed the user id in the key, so they never bleed
 * across accounts, and they MUST survive logout, user switch AND tenant/
 * portfolio switch (a per-user layout, not tenant state).
 *
 * ⇨ When you add a NEW persisted key anywhere, register it here under the correct
 *   scope (or extend a family matcher). Literal values mirror their write sites
 *   (this module is intentionally self-contained, out of feature-module imports).
 */
const SESSION_BOUND = {
  keys: [
    JWT_TOKEN,
    LAST_ACTIVITY, // cross-tab idle bookkeeping; must not outlive the session
    PORTFOLIO,
    "token", // SSO bearer written by SsoCallback (the app's canonical key is jwtToken)
    "currentUserWorkInfo", // current-user object incl. collaborator identity
    "term-overrides-cache", // tenant-specific terminology overrides
    "ganttScenarioFilters-V6", // scenario-scoped gantt filters
    // Date-range / year view presets (treated as session/env scoped).
    "Portfolio_Date_Filter",
    "fullTimelineYearFilter-V2",
    "customTimelineYearFilter-V2",
    "customSupplyYearFilter",
  ],
  prefixes: [
    "lastTenant:",
    PROJECT_DASHBOARD_FILTER, // suffixed by the JWT sub
    // GSEP scenario-scoped state — scenario ids are not unique across tenants/envs.
    "gsep-scopes-stale-",
    "gsep-assumptions-locked-",
    "gsep-quantities-snapshot-",
    "gsep_viewMode_",
  ],
  suffixes: [],
};

const USER_BOUND = {
  keys: [
    "Projects_Grid_Archived_Filter",
    "User_Taskboard_Date_Filter",
    "User_Taskboard_Team_Filter",
    "User_Taskboard_Late_Filter",
    "User_Taskboard_View",
    "myTasksSavedColumnsOrder",
    // Multi-project/timeline gantt column visibility. Was unregistered (so it
    // survived user switches and leaked one user's layout to the next login
    // on a shared browser) — it is a per-user layout, not device state.
    "columnsForTimeline-V9",
    "GANTT_TASK_FOCUS_FILTERS", // task-focus filter state (uppercase; not caught by the prefix)
    "timelineFilters-V2", // multi-project timeline filter selections
    "multiProjectEditingEnabled-V1", // editing-affordance preference — must not leak to another account on the same browser
    "selected-shifts", // shift filter selection
    // Workload page selections (unbranded generic keys).
    "selectedProjects",
    "selectedResources",
  ],
  prefixes: [
    "taskFocus", // taskFocusGanttColumns / taskFocusSavedColumnsOrder / taskFocusColumnsSavedAt
    // All scheduler filter selections: scheduler_filter_selected_projects/_teams,
    // _dates, _duration_driven_backlog_selected, _are_teams_view, …
    "scheduler_filter_",
  ],
  suffixes: [
    "_columnsState", // any grid's saved column layout
    "_columnsSavedAt",
    "ProjectsFilter-V2", // full/customTimelineProjectsFilter-V2
    "TagsFilter-V2", // full/customTimelineTagsFilter-V2
  ],
};

function removeMatching({ keys, prefixes, suffixes }) {
  const exact = new Set(keys);
  try {
    Object.keys(localStorage).forEach((k) => {
      if (
        exact.has(k) ||
        prefixes.some((p) => k.startsWith(p)) ||
        suffixes.some((s) => k.endsWith(s))
      ) {
        localStorage.removeItem(k);
      }
    });
  } catch {
    // localStorage unavailable — nothing to clear
  }
}

/** Wipe credential + tenant/environment-bound entries — call on logout. */
export function clearSessionScopedStorage() {
  removeMatching(SESSION_BOUND);
}

/** Wipe per-user preference/selection entries — call on a user switch. */
export function clearUserScopedStorage() {
  removeMatching(USER_BOUND);
}
